import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api';
import { errorMessage, useAsync } from '../../hooks/useAsync';
import { dateOnly, dateTime, fullName, money } from '../../services/format';
import { useOrgCurrencyCodes } from '../../hooks/useCurrencies';
import { Alert, Badge, Button, Card, Cell, Confirm, Field, Input, KeyValue, PageTitle, Row, Select, Table, Tabs, Textarea, statusTone } from '../../components/ui';
import { subscriptionLabel } from './OrgsList';
import { SubscriptionTab } from './SubscriptionTab';
import { VatDecisionButtons, VatStatusBadge, countries, decisionNotice } from '../../components/vat';
import { SavedCard } from '../../components/payment';
import { InvoiceAmounts, KindBadge, invoiceNumber } from '../../components/invoice';
import type { CustomerKind, OrgKind, OrgStatus, OrganizationAdminPatch, OrganizationDetail, PaymentMethod, VatDecision } from '../../types/api';

type Tab = 'overview' | 'subscription' | 'members' | 'claims' | 'invitations' | 'usage' | 'invoices';
const TABS: Tab[] = ['overview', 'subscription', 'members', 'claims', 'invitations', 'usage', 'invoices'];

export function OrgDetail() {
  const { id } = useParams();
  const orgId = Number(id);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = (TABS.includes(params.get('tab') as Tab) ? params.get('tab') : 'overview') as Tab;
  const setTab = (next: Tab) => setParams(next === 'overview' ? {} : { tab: next }, { replace: true });
  const { data: org, error, reload } = useAsync(() => api.organization(orgId), [orgId]);
  const [notice, setNotice] = useState<string | null>(null);
  const [fail, setFail] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const run = async (name: string, fn: () => Promise<unknown>, done?: string) => {
    setBusy(name);
    setFail(null);
    setNotice(null);
    try {
      await fn();
      if (done) setNotice(done);
      reload();
    } catch (err) {
      setFail(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  if (error) return <Alert>{error}</Alert>;
  if (!org) return <p className="text-faint">{t('common.loading')}</p>;

  return (
    <div>
      <PageTitle
        title={
          <>
            {org.name} <Badge tone={statusTone(org.status)}>{t(`orgs.status.${org.status}`)}</Badge>
          </>
        }
        subtitle={<span className="font-mono">#{org.id} · {t(`orgs.kind.${org.kind}`)} · {org.currency} · {subscriptionLabel(org.subscription, t)} · {t('common.count', { count: org.member_count })}</span>}
        actions={<Button variant="primary" onClick={() => navigate(`/invoices/new?organization_id=${org.id}`)}>{t('invoices.new')}</Button>}
      />
      {notice && <div className="mb-3"><Alert tone="good">{notice}</Alert></div>}
      {fail && <div className="mb-3"><Alert>{fail}</Alert></div>}
      <Tabs tabs={[{ id: 'overview', label: t('orgs.tabs.overview') }, { id: 'subscription', label: t('orgs.tabs.subscription'), count: org.orders.filter((o) => o.status === 'pending').length || undefined }, { id: 'members', label: t('orgs.tabs.members'), count: org.members.length }, { id: 'claims', label: t('orgs.tabs.claims'), count: org.claims.length }, { id: 'invitations', label: t('orgs.tabs.invitations'), count: org.invitations.length }, { id: 'usage', label: t('orgs.tabs.usage') }, { id: 'invoices', label: t('orgs.tabs.invoices'), count: org.invoices.length }]} active={tab} onChange={setTab} />
      <div className="mt-4">
        {tab === 'overview' && (
          <>
            <Overview org={org} busy={busy === 'save'} onSave={(patch) => run('save', () => api.patchOrganization(org.id, patch), t('common.saved'))} />
            <VatCard
              org={org}
              busy={busy}
              onDecided={(decision) => {
                setFail(null);
                setNotice(decisionNotice(decision, t));
                reload();
              }}
              onError={(err) => setFail(errorMessage(err))}
              setBusy={setBusy}
            />
            <CurrencyCard org={org} busy={busy === 'currency'} onSave={(currency) => run('currency', () => api.patchOrganization(org.id, { currency }), t('orgs.currencySaved', { currency }))} />
            <SavedCard org={org} busy={busy === 'revoke'} onRevoke={() => run('revoke', () => api.revokeCard(org.id), t('payments.revoked'))} />
            <ReviewSampleCard org={org} />
          </>
        )}
        {tab === 'subscription' && <SubscriptionTab org={org} busy={busy} run={run} />}
        {tab === 'members' && <Members org={org} busy={busy} run={run} />}
        {tab === 'claims' && <Claims org={org} busy={busy} run={run} />}
        {tab === 'invitations' && <Invitations org={org} busy={busy} run={run} />}
        {tab === 'usage' && <Usage org={org} />}
        {tab === 'invoices' && <Invoices org={org} />}
      </div>
    </div>
  );
}

export type Run = (name: string, fn: () => Promise<unknown>, done?: string) => Promise<void>;

/** What the organization is priced and invoiced in (sculpt-api PLAN-multi-currency.md C6): staff change it while it has
 * paid nothing, which moves a €0 Free to the same offer in that currency and re-prices pending orders; once it paid,
 * the API refuses (`currency_locked`, read as its sentence in `errors`). */
function CurrencyCard({ org, busy, onSave }: { org: OrganizationDetail; busy: boolean; onSave: (currency: string) => void }) {
  const { t } = useTranslation();
  const [currency, setCurrency] = useState(org.currency);
  useEffect(() => setCurrency(org.currency), [org.currency]);
  const options = useOrgCurrencyCodes(org.currency);
  return (
    <div className="mt-4">
      <Card title={t('orgs.currency')}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (currency !== org.currency) onSave(currency);
          }}
          className="flex flex-wrap items-end gap-2"
        >
          <Field label={t('orgs.currency')} hint={t('orgs.currencyHint')} className="w-72">
            <Select value={currency} onChange={(e) => setCurrency(e.target.value)} data-testid="org-currency">
              {options.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Select>
          </Field>
          <Button type="submit" busy={busy} disabled={currency === org.currency} data-testid="org-currency-save">
            {t('orgs.changeCurrency')}
          </Button>
        </form>
      </Card>
    </div>
  );
}

/** Whether the owner keeps the organization out of the review's sample: read only, the owner sets it from the portal. */
function ReviewSampleCard({ org }: { org: OrganizationDetail }) {
  const { t, i18n } = useTranslation();
  return (
    <div className="mt-4">
      <Card title={t('orgs.reviewSample.title')}>
        <p className="text-[12.5px] text-ink" data-testid="review-sample">
          {org.review_sample_opt_out_at ? t('orgs.reviewSample.out', { date: dateTime(org.review_sample_opt_out_at, i18n.language) }) : t('orgs.reviewSample.in')}
        </p>
      </Card>
    </div>
  );
}

function Overview({ org, busy, onSave }: { org: OrganizationDetail; busy: boolean; onSave: (patch: OrganizationAdminPatch) => void }) {
  const { t, i18n } = useTranslation();
  const [name, setName] = useState(org.name);
  const [kind, setKind] = useState<OrgKind>(org.kind);
  const [status, setStatus] = useState<OrgStatus>(org.status);
  const [billingEmail, setBillingEmail] = useState(org.billing_email ?? '');
  const [billingName, setBillingName] = useState(org.billing_name ?? '');
  const [billingAddress, setBillingAddress] = useState(org.billing_address ?? '');
  const [vat, setVat] = useState(org.vat_number ?? '');
  const [customerKind, setCustomerKind] = useState<CustomerKind | ''>(org.customer_kind ?? '');
  const [street, setStreet] = useState(org.billing_street ?? '');
  const [zip, setZip] = useState(org.billing_zip ?? '');
  const [city, setCity] = useState(org.billing_city ?? '');
  const [country, setCountry] = useState(org.billing_country ?? '');
  const [siren, setSiren] = useState(org.siren ?? '');
  const [registration, setRegistration] = useState(org.registration_number ?? '');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(org.payment_method);
  const [notes, setNotes] = useState(org.notes);
  useEffect(() => {
    setName(org.name);
    setKind(org.kind);
    setStatus(org.status);
    setBillingEmail(org.billing_email ?? '');
    setBillingName(org.billing_name ?? '');
    setBillingAddress(org.billing_address ?? '');
    setVat(org.vat_number ?? '');
    setCustomerKind(org.customer_kind ?? '');
    setStreet(org.billing_street ?? '');
    setZip(org.billing_zip ?? '');
    setCity(org.billing_city ?? '');
    setCountry(org.billing_country ?? '');
    setSiren(org.siren ?? '');
    setRegistration(org.registration_number ?? '');
    setPaymentMethod(org.payment_method);
    setNotes(org.notes);
  }, [org]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const patch: OrganizationAdminPatch = {
      name: name.trim(),
      kind,
      status,
      billing_email: billingEmail.trim() || null,
      billing_name: billingName.trim() || null,
      billing_address: billingAddress.trim() || null,
      vat_number: vat.trim() || null,
      customer_kind: customerKind || null,
      billing_street: street.trim() || null,
      billing_zip: zip.trim() || null,
      billing_city: city.trim() || null,
      billing_country: country || null,
      siren: siren.trim() || null,
      registration_number: registration.trim() || null,
      payment_method: paymentMethod,
      notes,
    };
    onSave(patch);
  };

  return (
    <form onSubmit={submit} className="grid gap-4 lg:grid-cols-2">
      <Card title={t('orgs.tabs.overview')} actions={<Button type="submit" variant="primary" busy={busy} data-testid="org-save">{t('common.save')}</Button>}>
        <div className="flex flex-col gap-3">
          <Field label={t('orgs.name')}>
            <Input required value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t('orgs.kindLabel')}>
              <Select value={kind} onChange={(e) => setKind(e.target.value as OrgKind)}>
                <option value="team">{t('orgs.kind.team')}</option>
                <option value="personal">{t('orgs.kind.personal')}</option>
              </Select>
            </Field>
            <Field label={t('orgs.statusLabel')} hint={status === 'suspended' ? t('orgs.suspendedWarning') : status === 'archived' ? t('orgs.archivedWarning') : undefined}>
              <Select value={status} onChange={(e) => setStatus(e.target.value as OrgStatus)}>
                <option value="active">{t('orgs.status.active')}</option>
                <option value="suspended">{t('orgs.status.suspended')}</option>
                <option value="archived">{t('orgs.status.archived')}</option>
              </Select>
            </Field>
          </div>
          <Field label={t('orgs.notes')} hint={t('orgs.notesHint')}>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Field>
        </div>
      </Card>
      <Card
        title={
          <>
            {t('orgs.billing')}{' '}
            <Badge tone={org.billing_complete ? 'good' : 'warn'}>{t(org.billing_complete ? 'profile.complete' : 'profile.incomplete')}</Badge>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <p className="text-[12px] text-muted">{t('profile.hint')}</p>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t('profile.kindLabel')}>
              <Select value={customerKind} onChange={(e) => setCustomerKind(e.target.value as CustomerKind | '')} data-testid="profile-kind">
                <option value="">—</option>
                <option value="company">{t('profile.kind.company')}</option>
                <option value="individual">{t('profile.kind.individual')}</option>
              </Select>
            </Field>
            <Field label={t('profile.paymentMethod')} hint={paymentMethod === 'transfer' ? t('profile.transferHint') : undefined}>
              <Select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value as PaymentMethod)} data-testid="profile-payment">
                <option value="card">{t('profile.payment.card')}</option>
                <option value="transfer">{t('profile.payment.transfer')}</option>
              </Select>
            </Field>
          </div>
          <Field label={customerKind === 'individual' ? t('profile.nameIndividual') : t('orgs.billingName')}>
            <Input value={billingName} onChange={(e) => setBillingName(e.target.value)} />
          </Field>
          <Field label={t('profile.street')}>
            <Input value={street} onChange={(e) => setStreet(e.target.value)} />
          </Field>
          <div className="grid grid-cols-[1fr_2fr] gap-3">
            <Field label={t('profile.zip')}>
              <Input value={zip} onChange={(e) => setZip(e.target.value)} />
            </Field>
            <Field label={t('profile.city')}>
              <Input value={city} onChange={(e) => setCity(e.target.value)} />
            </Field>
          </div>
          <Field label={t('profile.country')}>
            <Select value={country} onChange={(e) => setCountry(e.target.value)} data-testid="profile-country">
              <option value="">—</option>
              {countries(i18n.language).map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name} ({c.code})
                </option>
              ))}
            </Select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t('profile.siren')} hint={t('profile.sirenHint')}>
              <Input value={siren} onChange={(e) => setSiren(e.target.value)} />
            </Field>
            <Field label={t('orgs.vat')}>
              <Input value={vat} onChange={(e) => setVat(e.target.value)} data-testid="profile-vat" />
            </Field>
          </div>
          <Field label={t('profile.registration')} hint={t('profile.registrationHint')}>
            <Input value={registration} maxLength={40} onChange={(e) => setRegistration(e.target.value)} />
          </Field>
          <Field label={t('orgs.billingEmail')} hint={t('orgs.billingEmailHint')}>
            <Input type="email" value={billingEmail} onChange={(e) => setBillingEmail(e.target.value)} />
          </Field>
          <Field label={t('profile.legacyAddress')} hint={t('profile.legacyAddressHint')}>
            <Textarea value={billingAddress} onChange={(e) => setBillingAddress(e.target.value)} />
          </Field>
          <div className="font-mono text-[11px] text-faint">
            {t('orgs.createdAt')}: {dateTime(org.created_at)} · {t('orgs.updatedAt')}: {dateTime(org.updated_at)}
            {org.archived_at && <> · {t('orgs.archivedAt')}: {dateTime(org.archived_at)}</>}
          </div>
        </div>
      </Card>
    </form>
  );
}

/** What VIES said of the organization's VAT number, and the staff actions on it (PLAN-payments.md P9). */
function VatCard({ org, busy, setBusy, onDecided, onError }: { org: OrganizationDetail; busy: string | null; setBusy: (b: string | null) => void; onDecided: (d: VatDecision) => void; onError: (err: unknown) => void }) {
  const { t, i18n } = useTranslation();
  const l = i18n.language;
  if (!org.vat_number) return null;
  const check = async () => {
    setBusy('vies');
    try {
      onDecided(await api.checkVat(org.id));
    } catch (err) {
      onError(err);
    } finally {
      setBusy(null);
    }
  };
  const address = org.vat_check_address;
  return (
    <div className="mt-4">
      <Card
        title={
          <>
            {t('vat.cardTitle', { number: org.vat_number })} <VatStatusBadge status={org.vat_status} />
          </>
        }
        actions={
          <>
            <Button busy={busy === 'vies'} onClick={() => void check()} data-testid="vat-check">
              {t('vat.recheck')}
            </Button>
            <VatDecisionButtons organizationId={org.id} number={org.vat_number} onDone={onDecided} onError={onError} />
          </>
        }
      >
        {org.vat_status === 'review' && <div className="mb-3"><Alert tone="warn">{t('vat.reviewHint')}</Alert></div>}
        <KeyValue
          rows={[
            [t('vat.checkedAt'), dateTime(org.vat_checked_at, l)],
            [t('vat.registeredName'), org.vat_check_name ?? '—'],
            [t('vat.registeredAddress'), address ? <span className="whitespace-pre-line">{address}</span> : '—'],
            [t('vat.consultation'), org.vat_check_ref ? <span className="font-mono">{org.vat_check_ref}</span> : '—'],
            [
              t('vat.settled'),
              org.vat_decided_at
                ? org.vat_decided_by !== null
                  ? <Link to={`/users/${org.vat_decided_by}`} className="text-accent underline">{t('vat.settledByStaff', { date: dateTime(org.vat_decided_at, l) })}</Link>
                  : t('vat.settledByVies', { date: dateTime(org.vat_decided_at, l) })
                : '—',
            ],
          ]}
        />
      </Card>
    </div>
  );
}

function Members({ org, busy, run }: { org: OrganizationDetail; busy: string | null; run: Run }) {
  const { t, i18n } = useTranslation();
  const [transfer, setTransfer] = useState<number | null>(null);
  const target = org.members.find((m) => m.user_id === transfer);
  return (
    <>
      <Table head={[t('users.email'), t('users.name'), t('orgs.roleLabel'), t('users.verified'), t('users.active'), t('orgs.joinedAt'), t('users.lastConnection'), '']} empty={t('common.empty')}>
        {org.members.map((m) => (
          <Row key={m.user_id}>
            <Cell mono>
              <Link to={`/users/${m.user_id}`} className="text-accent underline">
                {m.email}
              </Link>
            </Cell>
            <Cell>{fullName(m.first_name, m.last_name, '—')}</Cell>
            <Cell>
              <Badge tone={m.role === 'owner' ? 'accent' : m.role === 'admin' ? 'good' : 'neutral'}>{t(`orgs.role.${m.role}`)}</Badge>
            </Cell>
            <Cell>{m.email_verified ? t('common.yes') : t('common.no')}</Cell>
            <Cell>{m.is_active ? t('common.yes') : t('common.no')}</Cell>
            <Cell mono>{dateTime(m.joined_at, i18n.language)}</Cell>
            <Cell mono>{dateTime(m.last_connection, i18n.language)}</Cell>
            <Cell>
              {m.role !== 'owner' && (
                <Button variant="ghost" onClick={() => setTransfer(m.user_id)}>
                  {t('orgs.makeOwner')}
                </Button>
              )}
            </Cell>
          </Row>
        ))}
      </Table>
      <Confirm open={transfer !== null} title={t('orgs.transferTitle')} message={t('orgs.transferMessage', { email: target?.email ?? '' })} confirmLabel={t('orgs.transferConfirm')} busy={busy === 'transfer'} onCancel={() => setTransfer(null)} onConfirm={() => transfer !== null && void run('transfer', () => api.transferOwnership(org.id, transfer), t('orgs.transferred')).then(() => setTransfer(null))} />
    </>
  );
}

function Claims({ org, busy, run }: { org: OrganizationDetail; busy: string | null; run: Run }) {
  const { t, i18n } = useTranslation();
  return (
    <>
      <p className="mb-3 text-[12.5px] text-muted">{t('orgs.claimsHint')}</p>
      <Table head={[t('orgs.claimKind'), t('orgs.claimValue'), t('orgs.autoJoin'), t('orgs.claimStatus'), t('orgs.createdAt'), '']} empty={t('orgs.noClaims')}>
        {org.claims.map((c) => (
          <Row key={c.id}>
            <Cell>{t(`orgs.claim.${c.kind}`)}</Cell>
            <Cell mono>{c.value}</Cell>
            <Cell>{c.auto_join ? t('common.yes') : t('common.no')}</Cell>
            <Cell>{c.verified_at ? <Badge tone="good">{t('orgs.verified')}</Badge> : <Badge tone="warn">{t('orgs.pending')}</Badge>}</Cell>
            <Cell mono>{dateTime(c.created_at, i18n.language)}</Cell>
            <Cell>
              <div className="flex justify-end gap-1">
                {!c.verified_at && (
                  <Button variant="primary" busy={busy === `verify-${c.id}`} onClick={() => void run(`verify-${c.id}`, () => api.verifyClaim(org.id, c.id), t('orgs.claimVerified'))}>
                    {t('orgs.verify')}
                  </Button>
                )}
                <Button variant="danger" busy={busy === `delclaim-${c.id}`} onClick={() => void run(`delclaim-${c.id}`, () => api.deleteClaim(org.id, c.id))}>
                  {t('common.delete')}
                </Button>
              </div>
            </Cell>
          </Row>
        ))}
      </Table>
    </>
  );
}

function Invitations({ org, busy, run }: { org: OrganizationDetail; busy: string | null; run: Run }) {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('member');
  const submit = (e: FormEvent) => {
    e.preventDefault();
    void run('invite', () => api.inviteToOrganization(org.id, email.trim(), role), t('orgs.invited', { email: email.trim() })).then(() => setEmail(''));
  };
  return (
    <div className="flex flex-col gap-4">
      <form onSubmit={submit} className="flex flex-wrap items-end gap-2 rounded-md border border-line-card bg-white p-3">
        <Field label={t('users.email')} className="w-72">
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field label={t('orgs.roleLabel')} className="w-36">
          <Select value={role} onChange={(e) => setRole(e.target.value)}>
            <option value="member">{t('orgs.role.member')}</option>
            <option value="admin">{t('orgs.role.admin')}</option>
          </Select>
        </Field>
        <Button type="submit" variant="primary" busy={busy === 'invite'}>
          {t('orgs.invite')}
        </Button>
      </form>
      <Table head={[t('users.email'), t('orgs.roleLabel'), t('orgs.sentAt'), t('orgs.expiresAt'), '']} empty={t('orgs.noInvitations')}>
        {org.invitations.map((i) => (
          <Row key={i.id}>
            <Cell mono>{i.email}</Cell>
            <Cell>{t(`orgs.role.${i.role}`)}</Cell>
            <Cell mono>{dateTime(i.created_at, i18n.language)}</Cell>
            <Cell mono>{dateTime(i.expires_at, i18n.language)}</Cell>
            <Cell>
              <div className="flex justify-end">
                <Button variant="danger" busy={busy === `revoke-${i.id}`} onClick={() => void run(`revoke-${i.id}`, () => api.revokeInvitation(org.id, i.id))}>
                  {t('orgs.revoke')}
                </Button>
              </div>
            </Cell>
          </Row>
        ))}
      </Table>
    </div>
  );
}

function Usage({ org }: { org: OrganizationDetail }) {
  const { t, i18n } = useTranslation();
  const u = org.usage;
  if (!u) return <p className="text-faint">{t('common.empty')}</p>;
  const ratio = u.unlimited || u.budget_usd <= 0 ? 0 : Math.min(1, u.spent_usd / u.budget_usd);
  return (
    <div className="flex flex-col gap-4">
      <Card title={t('orgs.tabs.usage')}>
        <div className="flex items-baseline justify-between">
          <span className="text-[20px] font-medium text-ink">{money(u.spent_usd, u.currency, i18n.language)}</span>
          <span className="font-mono text-[11px] text-faint">
            {u.unlimited ? t('orgs.unlimited') : `${t('billing.pool')} ${money(u.budget_usd, u.currency, i18n.language)} · ${t(`billing.period.${u.period}`)}`}
            {u.period_start && ` · ${dateOnly(u.period_start, i18n.language)} → ${dateOnly(u.period_end, i18n.language)}`}
          </span>
        </div>
        {!u.unlimited && (
          <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-hover" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(ratio * 100)}>
            <div className={`h-full rounded-full ${u.limit_reached ? 'bg-danger' : ratio > 0.8 ? 'bg-warn' : 'bg-accent'}`} style={{ width: `${ratio * 100}%` }} />
          </div>
        )}
        <div className="mt-2 font-mono text-[11px] text-faint">
          {t('users.requests')}: {u.request_count} · {t('billing.credits')}: {money(u.credits, u.currency, i18n.language)}
          {u.limit_reached && (
            <>
              {' '}
              · <Badge tone="bad">{u.reason ? t(`billing.reason.${u.reason}`) : t('orgs.limitReached')}</Badge>
            </>
          )}
        </div>
      </Card>
      <Table head={[t('users.email'), t('users.name'), t('users.spend'), t('billing.memberCap'), t('users.requests')]} empty={t('common.empty')}>
        {u.members.map((m) => (
          <Row key={m.user_id}>
            <Cell mono>
              <Link to={`/users/${m.user_id}`} className="text-accent underline">
                {m.email}
              </Link>
            </Cell>
            <Cell>{m.name}</Cell>
            <Cell mono>{money(m.spent_usd, u.currency, i18n.language)}</Cell>
            <Cell mono>{m.cap === null ? '—' : money(m.cap, u.currency, i18n.language)}</Cell>
            <Cell mono>{m.request_count}</Cell>
          </Row>
        ))}
      </Table>
    </div>
  );
}

function Invoices({ org }: { org: OrganizationDetail }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  return (
    <Table head={[t('invoices.number'), t('invoices.kind.label'), t('invoices.statusLabel'), t('invoices.amountExcl'), t('invoices.vat'), t('invoices.total'), t('invoices.issuedAt'), t('invoices.dueAt'), t('invoices.pdf')]} empty={t('common.empty')}>
      {org.invoices.map((i) => (
        <Row key={i.id} onClick={() => navigate(`/invoices/${i.id}`)}>
          <Cell mono className={i.number ? '' : 'text-faint'}>{invoiceNumber(i, t)}</Cell>
          <Cell>
            <KindBadge invoice={i} />
          </Cell>
          <Cell>
            <Badge tone={statusTone(i.status)}>{t(`invoices.status.${i.status}`)}</Badge>
          </Cell>
          <InvoiceAmounts invoice={i} />
          <Cell mono>{dateOnly(i.issued_at, i18n.language)}</Cell>
          <Cell mono>{dateOnly(i.due_at, i18n.language)}</Cell>
          <Cell>{i.has_pdf ? t('common.yes') : t('common.no')}</Cell>
        </Row>
      ))}
    </Table>
  );
}
